import { Scrollbar, useSettingsContext } from '@aibees/shared/components';
import { SHARED_ROUTES } from '@aibees/shared/constants';
import { useBoolean } from '@aibees/shared/hooks';
import { useProgressBarStore } from '@aibees/shared/store/progress-bar';
import { SxStyle } from '@aibees/shared/theme';
import { Box } from '@mui/material';
import { memo, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LAYOUT, SIDENAV } from '../config-layout';
import { Sidebar } from './components/sidebar';
import Header from './header';
import Main from './main';
import NavVertical from './nav-vertical';
import * as Styled from './styles';
import type { DashboardLayoutOptions } from './types';

type Props = {
  children: React.ReactNode;
  options: DashboardLayoutOptions;
  withOutZIndex?: boolean;
  mainSx?: SxStyle;
};

// ── Stable sx objects ──
const ROOT_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  height: '100vh',
};

const ROW_SX = { display: 'flex', flexDirection: 'row' as const };

const CONTENT_AREA_SX = {
  display: 'flex',
  flexDirection: { xs: 'column' as const, md: 'row' as const },
  flex: 1,
  height: '100%',
  mx: `${LAYOUT.SPACE}px`,
  mb: `${LAYOUT.SPACE}px`,
  gap: `${LAYOUT.SPACE * 1.6}px`,
  overflowY: 'auto' as const,
};

const MAIN_SCROLLBAR_SX = {
  height: 1,
  '& .MainContentScrollBar_Content': { flexGrow: 1 },
  '& .MainContentScrollBar_ContentWrapper': {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    '::-webkit-scrollbar': { display: 'none' },
  },
};

const MAIN_SCROLLBAR_CLASSNAMES = {
  contentEl: 'MainContentScrollBar_Content',
  contentWrapper: 'MainContentScrollBar_ContentWrapper',
};

export default memo(function DashboardLayout({
  children,
  options = {},
  withOutZIndex,
  mainSx,
}: Props) {
  const settings = useSettingsContext();
  const progressValue = useProgressBarStore.useValue();
  const nav = useBoolean(options.navigation?.header?.closable);
  const { pathname } = useLocation();
  const isPricingPage =
    pathname === SHARED_ROUTES.priceAndPlan ||
    pathname === SHARED_ROUTES.billingStatusSuccess ||
    pathname === SHARED_ROUTES.billingStatusCancel;

  useEffect(() => {
    settings.setMinimizeAble?.(!!options.navigation?.header?.closable);
  }, [options.navigation?.header?.closable, settings]);

  const rootSx = useMemo(
    () => ({
      ...ROOT_SX,
      gap: `${
        Number(String(options?.headerGap)) >= 0
          ? options.headerGap
          : LAYOUT.SPACE
      }px`,
    }),
    [options?.headerGap]
  );

  const columnSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column' as const,
      height: '100vh',
      width: !isPricingPage
        ? `calc(100vw - ${SIDENAV.W ? SIDENAV.W : 0}px)`
        : '100vw',
      marginLeft: `${SIDENAV.W && !isPricingPage ? SIDENAV.W : 0}px`,
    }),
    [isPricingPage]
  );

  return (
    <Box sx={rootSx}>
      {options.topElement}
      <Box sx={ROW_SX}>
        {SIDENAV.W && !isPricingPage && (
          <Sidebar options={options.sidebar} withOutZIndex={withOutZIndex} />
        )}
        <Box sx={columnSx}>
          <Header
            onOpenNav={nav.onTrue}
            options={options.header}
            withOutZIndex={withOutZIndex}
          />
          {options.showLoadingBar && (
            <Styled.LoadingBar
              variant="determinate"
              value={progressValue}
              aria-label="Page loading progress"
            />
          )}

          <Box sx={CONTENT_AREA_SX}>
            {!isPricingPage && !options.hideNavVertical && (
              <NavVertical
                openNav={nav.value}
                onCloseNav={nav.onFalse}
                options={options.navigation}
              />
            )}

            <Main sx={mainSx}>
              <Scrollbar
                className="MainContentScrollBar"
                classNames={MAIN_SCROLLBAR_CLASSNAMES}
                sx={MAIN_SCROLLBAR_SX}
              >
                {children}
              </Scrollbar>
            </Main>
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
